import React, { useEffect, useRef, useState } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import { VscDebugRestart } from "react-icons/vsc";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { FiChevronDown } from "react-icons/fi";
import "./PreviewPlaybackBar.css";
import { speedDropdownOptions } from "../Render/scrollSpeedMap";

const PreviewPlaybackBar = ({
  isPlaying,
  onPlayPause,
  onRestart,
  progress,
  currentLabel,
  durationLabel,
  onSeek,
  onSeekStart,
  onSeekEnd,
  speed,
  onSpeedChange,
  resolution,
  resolutionOptions = [],
  onResolutionChange,
  isFullscreen,
  onFullscreenToggle,
  isVisible = true,
  isCompact = false,
  onBarMouseEnter,
  onBarMouseLeave,
}) => {
  const [speedOpen, setSpeedOpen] = useState(false);
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const speedRef = useRef(null);
  const resolutionRef = useRef(null);
  const progressPct = Math.max(0, Math.min(100, (progress || 0) * 100));

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (speedRef.current && !speedRef.current.contains(e.target)) {
        setSpeedOpen(false);
      }
      if (resolutionRef.current && !resolutionRef.current.contains(e.target)) {
        setResolutionOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!isVisible || isCompact) {
      setSpeedOpen(false);
      setResolutionOpen(false);
    }
  }, [isVisible, isCompact]);

  // const barClass =
  //   "ppb-bar" +
  //   (isVisible ? "" : " is-hidden") +
  //   (isCompact ? " is-compact" : "");
   const barClass =
    "ppb-bar" +
    (isVisible ? "" : " is-hidden");

  return (
    <div
      className={barClass}
      data-html2canvas-ignore="true"
      aria-hidden={!isVisible}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onBarMouseEnter}
      onMouseLeave={onBarMouseLeave}
    >
      <div className="ppb-leading">
        <button
          type="button"
          className="ppb-btn ppb-btn-play"
          onClick={onPlayPause}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FaPause size={11} /> : <FaPlay size={11} />}
          <span>{isPlaying ? "Pause" : "Play"}</span>
        </button>

        {/* <span className="ppb-time ppb-time-compact">
          {currentLabel} / {durationLabel}
        </span> */}
      </div>

      <div className="ppb-extras" aria-hidden={isCompact}>
        <button
          type="button"
          className="ppb-btn"
          onClick={onRestart}
          aria-label="Restart"
          tabIndex={isCompact ? -1 : undefined}
        >
          <VscDebugRestart size={15} />
          <span>Restart</span>
        </button>

        <div className="ppb-seek-wrap">
          <input
            type="range"
            className="ppb-seek"
            min={0}
            max={1}
            step={0.001}
            value={Number.isFinite(progress) ? progress : 0}
            style={{ "--ppb-progress": `${progressPct}%` }}
            onMouseDown={onSeekStart}
            onTouchStart={onSeekStart}
            onChange={(e) => onSeek(Number(e.target.value))}
            onMouseUp={onSeekEnd}
            onTouchEnd={onSeekEnd}
            aria-label="Seek"
            tabIndex={isCompact ? -1 : undefined}
          />
        </div>

        {/* <span className="ppb-time">{currentLabel} / {durationLabel}</span> */}

        <div className="ppb-divider" aria-hidden="true" />

        <div className="ppb-speed" ref={speedRef}>
          <button
            type="button"
            className="ppb-speed-trigger"
            onClick={() => {
              setResolutionOpen(false);
              setSpeedOpen((o) => !o);
            }}
            aria-expanded={speedOpen}
            aria-haspopup="listbox"
            aria-label={`Speed ${speed}x`}
            tabIndex={isCompact ? -1 : undefined}
          >
            <span className="ppb-speed-label">Speed</span>
            <span className="ppb-speed-value">{speed}x</span>
            <FiChevronDown size={14} />
          </button>
          {speedOpen && !isCompact && (
            <div className="ppb-speed-menu" role="listbox">
              {speedDropdownOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={option.value === speed}
                  className={
                    "ppb-speed-option" +
                    (option.value === speed ? " is-active" : "")
                  }
                  onClick={() => {
                    onSpeedChange(option.value);
                    setSpeedOpen(false);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ppb-divider" aria-hidden="true" />

        <div className="ppb-speed ppb-resolution" ref={resolutionRef}>
          <button
            type="button"
            className="ppb-speed-trigger"
            onClick={() => {
              setSpeedOpen(false);
              setResolutionOpen((o) => !o);
            }}
            aria-expanded={resolutionOpen}
            aria-haspopup="listbox"
            aria-label={`Resolution ${resolution}`}
            tabIndex={isCompact ? -1 : undefined}
          >
            <span className="ppb-speed-label">Resolution</span>
            <span className="ppb-speed-value">{resolution}</span>
            <FiChevronDown size={14} />
          </button>
          {resolutionOpen && !isCompact && (
            <div className="ppb-speed-menu ppb-resolution-menu" role="listbox">
              {resolutionOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={option === resolution}
                  className={
                    "ppb-speed-option" +
                    (option === resolution ? " is-active" : "")
                  }
                  onClick={() => {
                    onResolutionChange?.(option);
                    setResolutionOpen(false);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ppb-divider" aria-hidden="true" />

        <button
          type="button"
          className="ppb-btn"
          onClick={onFullscreenToggle}
          aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          tabIndex={isCompact ? -1 : undefined}
        >
          {isFullscreen ? (
            <MdFullscreenExit size={18} />
          ) : (
            <MdFullscreen size={18} />
          )}
          <span>{isFullscreen ? "Exit" : "Fullscreen"}</span>
        </button>
      </div>
    </div>
  );
};

export default PreviewPlaybackBar;
